import {
	Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, ViewEncapsulation
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, skip, Subscription } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgIf, NgFor } from '@angular/common';

import { UNDERSCORE } from '../../../shared/utils/constant';
import { RDD_MODE } from '../../models/ecall-off-model';
import { NumberPipe } from '../../../shared/pipes/number.pipe';

export interface EcallOffHeaderForm {
	copyTo: string;
	customerCalloffReference: string;
	rddMode: string;
}

export interface EcallOffHeaderInit {
	formGroup: FormGroup;
}

@Component({
	encapsulation: ViewEncapsulation.Emulated,
	selector: 'copo-ecalloff-header',
	styleUrl: './ecall-off-header.component.css',
	templateUrl: './ecall-off-header.component.html',
	imports: [
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatFormFieldModule,
		TranslateModule,

		MatInputModule,

		MatButtonModule,
		MatIconModule,

		MatSelectModule,

		MatOptionModule,
		NgFor,

		MatTooltipModule,
		NumberPipe,
	],
})
export class EcallOffHeaderComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	@Input() copyTo?: string = '';
	@Input() customerCalloffReference?: string = '';
	@Input() editCopyTo?: boolean = false;
	@Input() editCustomerCalloffReference?: boolean = false;
	@Input() editRddMode?: boolean = false;
	@Input() nbOrderItems?: number = 0;
	@Input() objectTranslationKey?: string = 'ecall-off.batches';
	@Input() rddMode?: string = RDD_MODE[0];
	@Input() totalWeight?: number = 0;
	@Output() readonly formChange = new EventEmitter<EcallOffHeaderForm>();
	@Output() readonly init = new EventEmitter<EcallOffHeaderInit>();

	readonly RDD_MODES = RDD_MODE;
	readonly UNDERSCORE = UNDERSCORE;

	formGroup: FormGroup;
	formSubscription: Subscription;

	constructor() {
		this.formGroup = new FormGroup({
			copyTo: new FormControl('', [this.emailValidator.bind(this)]),
			customerCalloffReference: new FormControl('', [Validators.required]),
			rddMode: new FormControl(this.rddMode ?? RDD_MODE[0], [Validators.required])
		});
		this.formSubscription = this.formGroup.valueChanges
			.pipe(skip(1)) // skip ngOnInit patch value
			.pipe(debounceTime(500))
			.subscribe(form => {
				this.formChange.emit(<EcallOffHeaderForm> form);
			});
	}

	ngOnInit(): void {
		this.formGroup.patchValue({
			copyTo: this.copyTo,
			customerCalloffReference: this.customerCalloffReference,
			rddMode: this.rddMode
		});
		this.init.emit({
			formGroup: this.formGroup
		});
	}

	ngOnDestroy(): void {
		this.formSubscription.unsubscribe();
	}

	emailValidator(control: AbstractControl) {
		const emails = String(control.value).split(';').map(e => e.trim());
		const forbidden = emails.some(email => Validators.email(new FormControl(email)));
		return forbidden ? { toAddress: { value: <string> control.value } } : null;
	}

	getTooltipText(tooltipText: string) {
		if (tooltipText && tooltipText.length > 35) {
			const textArray = tooltipText.match(/.{1,35}/g);
			return textArray ? textArray.join('\n') : tooltipText;
		}
		return tooltipText;
	}
}
